// ResetPasswordPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import './AuthPages.css';

const API = process.env.REACT_APP_API_URL || "";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  useEffect(() => {
    // Verify token on mount
    if (!token) {
      setError('No reset token provided');
      setVerifying(false);
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await fetch(`${API}/auth/verify-reset-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || 'Invalid or expired token');
        }

        setTokenValid(true);
        setUserEmail(data.email || '');
      } catch (err) {
        console.error('[ResetPassword] Token verification error:', err);
        setError(err.message || 'Invalid or expired reset link');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  useEffect(() => {
    // Check password strength
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          new_password: password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }

      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { 
          state: { message: 'Password reset successful! Please login with your new password.' }
        });
      }, 3000);
    } catch (err) {
      console.error('[ResetPassword] Error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="auth-page">
        <div className="auth-container-modern">
          <div className="auth-loading">
            <div className="loading-spinner"></div>
            <p>Verifying reset link...</p>
          </div>
        </div>
        <div className="auth-background">
          <div className="bg-shape shape-1"></div>
          <div className="bg-shape shape-2"></div>
          <div className="bg-shape shape-3"></div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="auth-page">
        <div className="auth-container-modern">
          <div className="auth-header">
            <div className="auth-brand">
              <div className="brand-icon">
                <img src={`${process.env.PUBLIC_URL}/AdobeStock_133441382.png`} alt="Logo" className="brand-icon" />
              </div>
              <Link to="/" className="auth-link"><h1>BaoBrain</h1></Link>
            </div>
          </div>

          <div className="auth-error-container">
            <AlertCircle size={48} className="error-icon" />
            <h2>Invalid or Expired Link</h2>
            <p>{error || 'This password reset link is invalid or has expired.'}</p>
            <p>Please request a new password reset link.</p>
            
            <div className="auth-footer" style={{ marginTop: '2rem' }}>
              <Link to="/forgot-password" className="auth-btn auth-btn-primary">
                Request New Link
              </Link>
            </div>
          </div>
        </div>
        <div className="auth-background">
          <div className="bg-shape shape-1"></div>
          <div className="bg-shape shape-2"></div>
          <div className="bg-shape shape-3"></div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container-modern">
          <div className="auth-header">
            <div className="auth-brand">
              <div className="brand-icon">
                <img src={`${process.env.PUBLIC_URL}/AdobeStock_133441382.png`} alt="Logo" className="brand-icon" />
              </div>
              <Link to="/" className="auth-link"><h1>BaoBrain</h1></Link>
            </div>
          </div>

          <div className="auth-success-container">
            <div className="success-icon-wrapper">
              <CheckCircle className="success-icon" size={48} />
            </div>
            <h2>Password Reset Successful!</h2>
            <p className="success-message">
              Your password has been successfully reset.
            </p>
            <p className="success-subtitle">
              Redirecting you to login page...
            </p>
            <div className="loading-spinner" style={{ marginTop: '1rem' }}></div>
          </div>
        </div>
        <div className="auth-background">
          <div className="bg-shape shape-1"></div>
          <div className="bg-shape shape-2"></div>
          <div className="bg-shape shape-3"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container-modern">
        <div className="auth-header">
          <div className="auth-brand">
            <div className="brand-icon">
              <img src={`${process.env.PUBLIC_URL}/AdobeStock_133441382.png`} alt="Logo" className="brand-icon" />
            </div>
            <Link to="/" className="auth-link"><h1>BaoBrain</h1></Link>
          </div>
          <div className="auth-welcome">
            <h2>Reset your password</h2>
            <p>Enter your new password for {userEmail}</p>
          </div>
        </div>

        <div className="auth-form-container">
          <form className="auth-form-modern" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={20} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Password Strength Indicator */}
            {password && (
              <div className="password-strength">
                <p className="strength-label">Password strength:</p>
                <div className="strength-indicators">
                  <span className={`indicator ${passwordStrength.length ? 'valid' : ''}`}>
                    8+ characters
                  </span>
                  <span className={`indicator ${passwordStrength.uppercase ? 'valid' : ''}`}>
                    Uppercase
                  </span>
                  <span className={`indicator ${passwordStrength.lowercase ? 'valid' : ''}`}>
                    Lowercase
                  </span>
                  <span className={`indicator ${passwordStrength.number ? 'valid' : ''}`}>
                    Number
                  </span>
                  <span className={`indicator ${passwordStrength.special ? 'valid' : ''}`}>
                    Special char
                  </span>
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={20} />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="auth-error-modern">
                <AlertCircle size={16} />
                <span className="error-text">{error}</span>
              </div>
            )}

            <button
              type="submit"
              className="auth-btn auth-btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner"></div>
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        </div>
      </div>

      <div className="auth-background">
        <div className="bg-shape shape-1"></div>
        <div className="bg-shape shape-2"></div>
        <div className="bg-shape shape-3"></div>
      </div>
    </div>
  );
}